import { z } from '@harvestiq/zod';
import { makeBarchartResponseWrapperSchema } from '../barchart-response';
import { zCoercedBoolean } from '@harvestiq/zod';
import { expectTypeOf } from 'expect-type';

// https://www.barchart.com/ondemand/openapi#tag/Price-Data/operation/get-getFuturesOptions
export const getFuturesOptionsParamsSchema = z.object({
  root: z.string().optional(),
  contract: z.string().optional(),
  symbols: z.string().optional(),
  exchange: z.string().optional(),
  type: z.string().optional(),
  expirationMonth: z.coerce.number().optional(),
  expirationDay: z.coerce.number().optional(),
  fields: z.string().optional(),
  realtime: zCoercedBoolean().optional(),
  mode: z.enum(['I', 'D']).optional(), // I = intraday, D = daily
});

export type GetFuturesOptionsParams = z.infer<
  typeof getFuturesOptionsParamsSchema
>;

// backwards compatibility
export type IGetFuturesOptionsParams = GetFuturesOptionsParams;

/* example response item
{
    "symbol": "HEZ100C",
    "root": "HE",
    "contract": "HEZ24",
    "contractName": "Lean Hogs",
    "contractMonth": "Z",
    "exchange": "CME",
    "type": "Call",
    "strike": 10,
    "expirationDate": "2024-12-13",
    "date": "2024-12-02",
    "impliedVolatility": 0,
    "delta": 0,
    "gamma": 0,
    "theta": -0.0123,
    "vega": 0,
    "open": 0,
    "high": 73.225,
    "low": 73.225,
    "last": 73.225,
    "previousClose": 72.075,
    "change": 1.15,
    "percentChange": 1.6,
    "volume": "0",
    "settlement": null,
    "previousSettlement": null
}
*/

export const futuresOptionsRawSchema = z.object({
  symbol: z.string().nullable(),
  root: z.string().nullable(),
  contract: z.string().nullable(),
  contractName: z.string().nullable(),
  contractMonth: z.string().nullable(),
  exchange: z.string().nullable(),
  type: z.string().nullable(),
  strike: z.number().nullable(),
  expirationDate: z.string().nullable(),
  date: z.string().nullable(),
  impliedVolatility: z.number().optional().nullable(),
  delta: z.number().optional().nullable(),
  gamma: z.number().optional().nullable(),
  theta: z.number().optional().nullable(),
  vega: z.number().optional().nullable(),
  open: z.number().nullable().nullable(),
  high: z.number().nullable().nullable(),
  low: z.number().nullable().nullable(),
  last: z.number().nullable().nullable(),
  previousClose: z.number().nullable(),
  change: z.number().nullable(),
  percentChange: z.number().nullable(),
  volume: z.string().nullable(),
  settlement: z.number().optional().nullable(),
  previousSettlement: z.number().optional().nullable(),
  extendedSymbol: z.string().optional().nullable(),
  bid: z.number().optional().nullable(),
  bidSize: z.number().optional().nullable(),
  ask: z.number().optional().nullable(),
  askSize: z.number().optional().nullable(),
  premium: z.number().optional().nullable(),
  openInterest: z.number().optional().nullable(),
});

// the specs show that a lot of the fields returned are nullable
// but we will filter out the null values in the response since those are not useful
export const futuresOptionsSchema = futuresOptionsRawSchema
  .omit({
    symbol: true,
    root: true,
    contract: true,
    contractName: true,
    contractMonth: true,
    exchange: true,
    type: true,
    strike: true,
    expirationDate: true,
    date: true,
    last: true,
  })
  .extend({
    symbol: z.string(),
    root: z.string(),
    contract: z.string(),
    contractName: z.string(),
    contractMonth: z.string(),
    exchange: z.string(),
    type: z.string(),
    strike: z.number(),
    expirationDate: z.string(),
    date: z.string(),
    last: z.number(),
  });

type GetFuturesOptionsRaw = z.infer<typeof futuresOptionsRawSchema>;

export type GetFuturesOptions = z.infer<typeof futuresOptionsSchema>;

expectTypeOf<keyof GetFuturesOptionsRaw>().toMatchTypeOf<
  keyof GetFuturesOptions
>();

// backwards compatibility
export type IGetFuturesOptionsResult = GetFuturesOptions;

// array of futures options
export const futuresOptionsResponseSchema =
  makeBarchartResponseWrapperSchema(futuresOptionsSchema);

export type FuturesOptionsResponse = z.infer<
  typeof futuresOptionsResponseSchema
>;
